import React from "react";
import { renderPhiStatusTag } from "../lib/phiStatusTags";

interface MessageContentProps {
    content: string;
    isUserMessage?: boolean;
    patientName?: string;
    phiAnonymized?: boolean;
    phiDeanonymized?: boolean;
    phiDeanonymizationEnabled?: boolean;
    phiEncryptedMapAvailable?: boolean;
    phiViolation?: boolean;
}

/**
 * Renders chat message content with support for:
 * - Markdown headers (# ## ###)
 * - Code blocks (```language\ncode\n```)
 * - Inline code (`code`)
 * - Bold text (**text**)
 */
export const CindraChatMessageContent: React.FC<MessageContentProps> = ({ content, isUserMessage = false, patientName, phiAnonymized, phiDeanonymized, phiDeanonymizationEnabled, phiEncryptedMapAvailable, phiViolation }) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        const trimmedLine = line.trim();

        // Check for code block start (```language or ```)
        if (trimmedLine.startsWith('```')) {
            const language = trimmedLine.substring(3).trim() || 'plaintext';
            const codeLines: string[] = [];
            i++; // Move past the opening ```

            // Collect all lines until closing ```
            while (i < lines.length && !lines[i].trim().startsWith('```')) {
                codeLines.push(lines[i]);
                i++;
            }

            // Render code block with patient tag and PHI status tags
            const phiTag = renderPhiStatusTag(phiAnonymized, phiDeanonymized, phiDeanonymizationEnabled, phiEncryptedMapAvailable, phiViolation);
            elements.push(
                <div key={`code-${i}`} className="my-2 rounded-md overflow-hidden relative">
                    {(patientName || phiTag) && (
                        <div className="absolute top-3 right-3 z-10 ml-2 flex gap-1">
                            {patientName && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                                    {patientName}
                                </span>
                            )}
                            {phiTag}
                        </div>
                    )}
                    <div className="bg-gray-800 text-gray-200 px-3 py-1 text-xs font-mono border-b border-gray-700">
                        {language}
                    </div>
                    <pre className="bg-gray-900 text-gray-100 p-3 overflow-x-auto text-sm font-mono" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
                        <code style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>{codeLines.join('\n')}</code>
                    </pre>
                </div>
            );

            i++; // Move past the closing ```
            continue;
        }

        // Render regular line with inline formatting
        elements.push(
            <p key={`line-${i}`} className="mb-2 last:mb-0 select-text">
                {renderInlineFormatting(line)}
            </p>
        );

        i++;
    }

    // Check if there are any code blocks (which already have their own tags)
    const hasCodeBlock = elements.some(el => (el as any)?.key?.startsWith('code-'));

    // Determine if content is "significant" (more than just a simple header/intro)
    const contentText = content.trim();
    const contentLines = contentText.split('\n').filter(line => line.trim());
    const isSignificantContent = contentLines.length > 1 || contentText.length > 50;

    const phiTagForRegularContent = renderPhiStatusTag(phiAnonymized, phiDeanonymized, phiDeanonymizationEnabled, phiEncryptedMapAvailable, phiViolation);

    return (
        <div className="prose prose-sm max-w-none relative" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
            {!hasCodeBlock && isSignificantContent && !isUserMessage && (patientName || phiTagForRegularContent) && (
                <div className="float-right ml-2 flex gap-1">
                    {patientName && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                            {patientName}
                        </span>
                    )}
                    {phiTagForRegularContent}
                </div>
            )}
            {elements}
        </div>
    );
};

/**
 * Renders a single line with inline formatting:
 * - Headers (# ## ###)
 * - Inline code (`code`)
 * - Bold text (**text**)
 */
function renderInlineFormatting(line: string): React.ReactNode {
    const trimmedLine = line.trim();

    // Check for headers
    if (trimmedLine.startsWith("# ")) {
        return <strong className="text-lg select-text">{trimmedLine.substring(2)}</strong>;
    }
    if (trimmedLine.startsWith("## ")) {
        return <strong className="text-base select-text">{trimmedLine.substring(3)}</strong>;
    }
    if (trimmedLine.startsWith("### ")) {
        return <strong className="text-sm select-text">{trimmedLine.substring(4)}</strong>;
    }

    // Parse inline code and bold
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let key = 0;

    // Pattern to match: `code` or **bold**
    const inlinePattern = /(`[^`]+`|\*\*[^*]+\*\*)/g;
    let match;
    let lastIndex = 0;

    while ((match = inlinePattern.exec(remaining)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            parts.push(
                <span key={`text-${key++}`} className="select-text">
                    {remaining.substring(lastIndex, match.index)}
                </span>
            );
        }

        const matchedText = match[0];

        // Inline code
        if (matchedText.startsWith('`') && matchedText.endsWith('`')) {
            parts.push(
                <code
                    key={`code-${key++}`}
                    className="bg-gray-800 text-gray-100 px-1.5 py-0.5 rounded text-xs font-mono select-text"
                >
                    {matchedText.slice(1, -1)}
                </code>
            );
        }
        // Bold
        else if (matchedText.startsWith('**') && matchedText.endsWith('**')) {
            parts.push(
                <strong key={`bold-${key++}`} className="select-text">
                    {matchedText.slice(2, -2)}
                </strong>
            );
        }

        lastIndex = match.index + matchedText.length;
    }

    // Add remaining text
    if (lastIndex < remaining.length) {
        parts.push(
            <span key={`text-${key++}`} className="select-text">
                {remaining.substring(lastIndex)}
            </span>
        );
    }

    return parts.length > 0 ? <>{parts}</> : <span className="select-text">{line}</span>;
}
